import { Component, Inject, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, HostListener } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { admins } from '../../../environments/environment';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { RouterModule } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { Subject, takeUntil, filter } from 'rxjs';

import { AdminService } from '../../shared/services/admin/admin.service';
import { LoadingStateService } from '../../shared/services/layout/loading-state.service';
import { UserProfileService } from '../../core/auth/services/user-profile.service';
import { AuthService } from '../../core/auth/services/auth.service';
import { HeaderNavigationService, DropdownLabelService } from '../../shared/services';
import {
  Tab,
  TabConfig,
  DropdownItem
} from '../../shared/models';
import {
  SERVICE_FLAGS,
  ADMIN_TITLES,
  CALCULATOR_TITLES
} from '../../shared/constants/layout.constants';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css']
})
export class HeaderComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('tabsContainer') tabsContainer!: ElementRef<HTMLDivElement>;
  private readonly destroy$ = new Subject<void>();
  userName: string = '';
  firstName: string = '';
  lastName: string = '';
  email: string = '';
  profilePictureUrl: string | null = null;
  static userProfileName: string;
  isAdminUser: boolean = false;
  activeTabIndex: number = 0;
  isAdminPage: boolean = false;
  shouldShowTabs: boolean = true;
  isMobileMenuOpen: boolean = false;
  canScrollPrev: boolean = false;
  canScrollNext: boolean = false;

  // Tab configurations following Single Responsibility Principle
  private readonly tabConfigs: Record<string, TabConfig> = {
    'premix': { key: 'premix', title: CALCULATOR_TITLES.PREMIX, serviceFlag: 'premixShow', activeIndex: 0 },
    'jarmix': { key: 'jarmix', title: CALCULATOR_TITLES.JARMIX, serviceFlag: 'jarmixShow', activeIndex: 1 },
    'ats': { key: 'ats', title: CALCULATOR_TITLES.ATS, serviceFlag: 'atsShow', activeIndex: 2 },
    'technical': { key: 'technical', title: CALCULATOR_TITLES.TECHNICAL, serviceFlag: 'techGuideShow', activeIndex: 3 },
    'program-builder': { key: 'program-builder', title: CALCULATOR_TITLES.PROGRAM_BUILDER, serviceFlag: 'programBuilderShow', activeIndex: 4 },
    'resistance-management': { key: 'resistance-management', title: CALCULATOR_TITLES.RESISTANCE, serviceFlag: 'resistanceShow', activeIndex: 5 },
    'admin-premix': { key: 'admin-premix', title: ADMIN_TITLES.PREMIX, activeIndex: 0, adminServiceIndex: 0 },
    'admin-technical': { key: 'admin-technical', title: ADMIN_TITLES.TECHNICAL, activeIndex: 1, adminServiceIndex: 1 },
    'admin-resistance-management': { key: 'admin-resistance-management', title: ADMIN_TITLES.RESISTANCE, activeIndex: 2, adminServiceIndex: 2 }
  };

  constructor(
    private userProfileService: UserProfileService,
    @Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
    public adminService: AdminService,
    private router: Router,
    private authService: AuthService,
    private headerNavService: HeaderNavigationService,
    private dropdownLabelService: DropdownLabelService,
    private loadingStateService: LoadingStateService,
    private titleService: Title
  ) { }

  ngOnInit(): void {
    this.initializeUserProfile();
    this.initializeRouterSubscription();
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    // Check scroll state after view initialization
    setTimeout(() => this.checkScrollState(), 100);
    // Also check after a longer delay to ensure all content is rendered
    setTimeout(() => this.checkScrollState(), 500);
  }

  @HostListener('window:resize')
  onResize(): void {
    // Recheck scroll state when window is resized
    setTimeout(() => this.checkScrollState(), 100);
  }

  // Public getters for template
  get dropdownItems(): DropdownItem[] {
    const tabs = this.getCurrentTabs();
    return tabs.map(tab => ({
      key: tab.key,
      title: tab.title,
      displayText: this.getDisplayTextForDropdown(tab.key, tab.title)
    }));
  }

  get shouldShowScrollArrows(): boolean {
    return this.canScrollPrev || this.canScrollNext;
  }

  // Public methods for template
  getCurrentTabs(): Tab[] {
    return this.headerNavService.getTabs(this.isAdminPage);
  }

  getCurrentActiveTab(): Tab | undefined {
    const tabs = this.getCurrentTabs();
    return tabs[this.activeTabIndex];
  }

  getUserInitials(): string {
    if (this.firstName && this.lastName) {
      return (this.firstName.charAt(0) + this.lastName.charAt(0)).toUpperCase();
    }
    if (this.userName) {
      const names = this.userName.split(' ');
      if (names.length >= 2) {
        return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
      }
      return this.userName.charAt(0).toUpperCase();
    }
    return 'U';
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  isActiveTab(key: string): boolean {
    const tabs = this.getCurrentTabs();
    return tabs[this.activeTabIndex]?.key === key;
  }

  trackByKey(index: number, item: any): string {
    return item.key;
  }

  selectCalculator(calculator: string): void {
    const config = this.tabConfigs[calculator];
    if (!config) return;
    this.isMobileMenuOpen = false; // Close mobile menu after selection
    if (this.isAdminPage) {
      this.handleAdminTabSelection(config);
    } else {
      this.handleRegularTabSelection(config);
    }
    // Check scroll state after tab selection
    setTimeout(() => this.checkScrollState(), 100);
  }

  // Public method to force scroll state check (useful for debugging)
  forceScrollStateCheck(): void {
    this.checkScrollState();
  }

  async logout(): Promise<void> {
    try {
      // Show logout loading state
      this.loadingStateService.showLoading('Signing out...', 'logout');
      // Add a small delay to show the loading state
      await new Promise(resolve => setTimeout(resolve, 500));
      await this.authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
      this.loadingStateService.hideLoading();
    }
  }

  // Navigation scroll methods
  scrollTabs(direction: 'prev' | 'next'): void {
    if (!this.tabsContainer) return;
    const container = this.tabsContainer.nativeElement;
    const scrollAmount = 200; // Adjust scroll distance as needed
    if (direction === 'prev') {
      container.scrollLeft -= scrollAmount;
    } else {
      container.scrollLeft += scrollAmount;
    }
    // Update scroll state after animation
    setTimeout(() => this.checkScrollState(), 300);
  }

  checkScrollState(): void {
    if (!this.tabsContainer) return;
    const container = this.tabsContainer.nativeElement;
    const hasHorizontalScroll = container.scrollWidth > container.clientWidth;
    this.canScrollPrev = container.scrollLeft > 0;
    this.canScrollNext = container.scrollLeft < (container.scrollWidth - container.clientWidth);
    // If there's no overflow, hide both arrows
    if (!hasHorizontalScroll) {
      this.canScrollPrev = false;
      this.canScrollNext = false;
    }
    setTimeout(() => {
      // Double-check after a small delay to ensure DOM is updated
      const updatedHasScroll = container.scrollWidth > container.clientWidth;
      if (!updatedHasScroll) {
        this.canScrollPrev = false;
        this.canScrollNext = false;
      }
    }, 50);
  }

  // Private initialization methods following Single Responsibility Principle
  private initializeUserProfile(): void {
    this.userProfileService.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe((userProfile: any) => {
        this.userName = userProfile?.fullName as string;
        this.firstName = userProfile?.firstName as string;
        this.lastName = userProfile?.lastName as string;
        this.email = (userProfile?.email as string)?.toLowerCase() || '';
        HeaderComponent.userProfileName = this.userName;
        this.isAdminUser = !!this.email && admins.emailAddress.includes(this.email);
      });

    this.userProfileService.profilePicture$
      .pipe(takeUntil(this.destroy$))
      .subscribe((profilePictureUrl: string | null) => {
        this.profilePictureUrl = profilePictureUrl;
      });
  }

  private initializeRouterSubscription(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        const navigationEvent = event as NavigationEnd;
        this.updateNavigationState(navigationEvent.url);
      });
  }

  private initializeComponent(): void {
    this.updateNavigationState(this.router.url);
  }

  private updateNavigationState(url: string): void {
    this.isAdminPage = this.headerNavService.isAdminRoute(url);
    this.shouldShowTabs = this.headerNavService.shouldShowTabs(url);
    this.updateActiveTabIndex(url);
    this.isMobileMenuOpen = false; // Close mobile menu on route change
    // Check scroll state when navigation changes
    setTimeout(() => this.checkScrollState(), 200);
  }

  // Private helper methods following Open/Closed Principle
  private getDisplayTextForDropdown(key: string, title: string): string {
    return this.dropdownLabelService.getDisplayText(key, title);
  }

  private handleAdminTabSelection(config: TabConfig): void {
    if (config.adminServiceIndex !== undefined) {
      this.adminService.activeIndex = config.adminServiceIndex;
    }
    this.adminService.title = config.title;
    this.activeTabIndex = config.activeIndex;
    this.updatePageTitle(config.title);
    // Check scroll state after admin tab selection
    setTimeout(() => this.checkScrollState(), 100);
  }

  private handleRegularTabSelection(config: TabConfig): void {
    this.resetAllServiceFlags();
    if (config.serviceFlag) {
      (this.adminService as any)[config.serviceFlag] = true;
    }
    this.adminService.title = config.title;
    this.activeTabIndex = config.activeIndex;
    this.updatePageTitle(config.title);
    // Check scroll state after regular tab selection
    setTimeout(() => this.checkScrollState(), 100);
  }

  private resetAllServiceFlags(): void {
    this.adminService.premixShow = false;
    this.adminService.jarmixShow = false;
    this.adminService.atsShow = false;
    this.adminService.techGuideShow = false;
    this.adminService.programBuilderShow = false;
    this.adminService.resistanceShow = false;
  }

  private updateActiveTabIndex(url?: string): void {
    if (this.isAdminPage) {
      this.updateAdminTabIndex(url);
    } else {
      this.updateRegularTabIndex(url);
    }
  }

  private updateAdminTabIndex(url?: string): void {
    const tabTitleMap: { [key: number]: string } = {
      0: ADMIN_TITLES.PREMIX,
      1: ADMIN_TITLES.TECHNICAL,
      2: ADMIN_TITLES.RESISTANCE
    };
    if (url) {
      const normalizedUrl = url.split('?')[0].split('#')[0];
      if (normalizedUrl.includes('admin-technical') || normalizedUrl.includes('technical-guide')) {
        this.adminService.activeIndex = 1;
      } else if (normalizedUrl.includes('admin-resistance') || normalizedUrl.includes('resistance-management')) {
        this.adminService.activeIndex = 2;
      } else {
        this.adminService.activeIndex = 0;
      }
    }
    this.activeTabIndex = this.adminService.activeIndex;
    this.adminService.title = tabTitleMap[this.activeTabIndex] || '';
    this.updatePageTitle(this.adminService.title);
  }

  private updateRegularTabIndex(url?: string): void {
    if (url) {
      this.resetAllServiceFlags();
      const normalizedUrl = url.split('?')[0].split('#')[0];
      const matchingConfig = Object.values(this.tabConfigs).find(config =>
        normalizedUrl.includes(config.key) && !config.key.startsWith('admin-')
      );
      if (matchingConfig && matchingConfig.serviceFlag) {
        (this.adminService as any)[matchingConfig.serviceFlag] = true;
        this.activeTabIndex = matchingConfig.activeIndex;
        this.adminService.title = matchingConfig.title;
      } else {
        this.adminService.premixShow = true;
        this.activeTabIndex = 0;
        this.adminService.title = CALCULATOR_TITLES.PREMIX;
      }
      this.updatePageTitle(this.adminService.title);
      return;
    }
    const activeService = this.findActiveService();
    if (activeService) {
      const config = Object.values(this.tabConfigs).find(config =>
        config.serviceFlag === activeService.flag
      );
      if (config) {
        this.activeTabIndex = config.activeIndex;
        this.adminService.title = config.title;
        return;
      }
    }
    // Default to premix if no tab is active
    this.adminService.premixShow = true;
    this.activeTabIndex = 0;
    this.adminService.title = CALCULATOR_TITLES.PREMIX;
  }

  private updatePageTitle(tabTitle: string): void {
    if (!tabTitle) return;
    const formatted = tabTitle
      .toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase());
    this.titleService.setTitle(`Syngenta | ${formatted}`);
  }

  private findActiveService(): { flag: keyof AdminService } | null {
    for (const flag of SERVICE_FLAGS) {
      if ((this.adminService as any)[flag]) {
        return { flag };
      }
    }
    return null;
  }



  // Admin badge navigation methods
  getAdminBadgeRoute(): string {
    return this.isAdminPage ? '/' : '/admin-home';
  }

  getAdminBadgeText(): string {
    return this.isAdminPage ? 'Home' : 'Admin';
  }

  getAdminBadgeTitle(): string {
    return this.isAdminPage ? 'Go to Home Page' : 'Go to Admin Panel';
  }

  getAdminBadgeAriaLabel(): string {
    return this.isAdminPage ? 'Navigate to Home Page' : 'Navigate to Admin Panel';
  }
}
